/* eslint-disable no-underscore-dangle */
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isFunctionLazyLoadable } from '../../Utils/utils';

const LottieLoader = () => {
  const dispatch = useDispatch();
  const enterPhase = useSelector((store) => store.transition.enterPhase);
  const currentPage = useSelector((store) => store.navigation.currentPage);
  const loaderConfig = useSelector(
    (store) => store.lottieAnimationData.loaderConfig
  );

  const [loadLottieFilesRenderer, setLoadLottieFilesRenderer] = useState(false);
  const [animationJsonFileLoaded, setAnimationJsonFileLoaded] = useState(false);

  const lottieAnimationRendererUrl =
    'https://unpkg.com/@lottiefiles/lottie-player@1.6.0/dist/lottie-player.js';

  useEffect(() => {
    /* Note: To appease Core Web Vitals, load lottie dependencies after user interactions
     * Load the first of two lottie dependencies after the enterPhase for reaching page 2 completes
     * Splitting up the loading of lottie resources minimises the chance for a UI lag during the chunk load
     */
    if (
      typeof currentPage === 'number' &&
      enterPhase === false &&
      currentPage > 0 &&
      !animationJsonFileLoaded &&
      !loadLottieFilesRenderer
    ) {
      /**
       * If there is no lottie animation json file, then preload it to reduce later latency and race conditions
       */
      import(
        './lazyLoadable/loadRemoteAnimationJsonFiles' /* webpackChunkName: 'loadRemoteAnimationJsonFiles' */
      )
        .then((lottieLoaderModule) => {
          if (
            isFunctionLazyLoadable({
              functionName: 'loadRemoteAnimationJsonFiles',
              module: lottieLoaderModule,
            })
          ) {
            lottieLoaderModule.loadRemoteAnimationJsonFiles(
              loaderConfig,
              dispatch
            );
            setAnimationJsonFileLoaded(true);
          }
        })
        .catch((error) => {
          /* eslint-disable no-console */
          console.log(
            'MVF App Info - loadRemoteAnimationJsonFiles failed to load:',
            error,
            '/nRe-trying shortly ...'
          );
          // Employ re-try strategy via setInterval (every 2 seconds until the request succeeds. Once it does, cleanup the interval)
          let pollingId = setInterval(() => {
            import(
              './lazyLoadable/loadRemoteAnimationJsonFiles' /* webpackChunkName: 'loadRemoteAnimationJsonFiles' */
            )
              .then((lottieLoaderModule) => {
                clearInterval(pollingId); // Prevent this code from running successfully more than once
                pollingId = null; // Release pollingId from the variable
                if (
                  isFunctionLazyLoadable({
                    functionName: 'loadRemoteAnimationJsonFiles',
                    module: lottieLoaderModule,
                  })
                ) {
                  lottieLoaderModule.loadRemoteAnimationJsonFiles(
                    loaderConfig,
                    dispatch
                  );
                  setAnimationJsonFileLoaded(true);
                }
              })
              .catch((retryError) => {
                // Do nothing and try again in 2 seconds
                /* eslint-disable no-console */
                console.log(
                  'loadRemoteAnimationJsonFiles failed to load again!:',
                  retryError,
                  '/nRe-trying shortly ...'
                );
              });
          }, 2000);
        });
    }
  }, [enterPhase]);

  useEffect(() => {
    /* Load the second of two lottie dependencies once the first lottie dependency is loaded
     * This leverages the 400ms-time window of the Transition animation during which the UI is unresponsive anyway
     */
    if (
      typeof currentPage === 'number' &&
      animationJsonFileLoaded &&
      !loadLottieFilesRenderer
    ) {
      setLoadLottieFilesRenderer(true);
    }
  }, [animationJsonFileLoaded]);

  return loadLottieFilesRenderer ? (
    <Script
      src={lottieAnimationRendererUrl}
      onLoad={() => {
        import(
          './lazyLoadable/dispatchAnimationData' /* webpackChunkName: 'dispatchAnimationData' */
        ).then((animationDispatchModule) => {
          if (
            isFunctionLazyLoadable({
              functionName: 'dispatchAnimationData',
              module: animationDispatchModule,
            })
          ) {
            animationDispatchModule.dispatchAnimationData({
              dispatch,
              payload: {
                isRendererAvailable: true,
              },
            });
          }
        });
      }}
      onError={() => {
        console.log('MVF App Info - Failed to fetch optional dependency');
        // Employ re-try strategy via setInterval (every 2 seconds until the request succeeds. Once it does, cleanup the interval)
        let pollingId = setInterval(() => {
          import(
            './lazyLoadable/dispatchAnimationData' /* webpackChunkName: 'dispatchAnimationData' */
          )
            .then((animationDispatchModule) => {
              clearInterval(pollingId); // Prevent this code from running successfully more than once
              pollingId = null; // Release pollingId from the variable
              if (
                isFunctionLazyLoadable({
                  functionName: 'dispatchAnimationData',
                  module: animationDispatchModule,
                })
              ) {
                animationDispatchModule.dispatchAnimationData({
                  dispatch,
                  payload: {
                    isRendererAvailable: true,
                  },
                });
              }
            })
            .catch((error) => {
              // Do nothing and try again in 2 seconds
              console.log(
                'MVF App Info - dispatchAnimationData failed to load:',
                error,
                '/nRe-trying shortly ...'
              );
            });
        }, 2000);
      }}
    />
  ) : null;
};

export default LottieLoader;
